'use client'

import { useState, type ReactNode } from 'react'
import * as m from 'motion/react-m'
import { Motion } from '@/lib/motion/lazy-motion'
import Hero from '@/components/content/common/Hero'
import Link from '@/components/ui/Link'
import Container from '@/components/structure/Container'
import VideoControls from '@/components/content/modules/hero/controls/VideoControls'
import Award from '@/components/ui/Award'
import {
  Classification,
  Recommendation,
} from '@/collections/JunctionTables/AppCategoryRecommendation/constants'
import { getBrandColors, hexToRgba, scrollToSection } from '@/lib/utils'
import type { App, AppCategoryRecommendation, Category } from '@/payload-types'

type CoursesHeroProps = {
  app: App
  children: ReactNode
}

export default function CoursesHero({ app, children }: CoursesHeroProps) {
  const [playing, setPlaying] = useState(false)
  const [muted, setMuted] = useState(true)
  const [modal, setModal] = useState(false)

  const { primaryColor, secondaryColor } = getBrandColors(app)

  const appCategories = (app.categoryRecommendations?.docs ?? []) as Array<
    AppCategoryRecommendation & {
      category: Pick<Category, 'name' | 'slug'>
    }
  >
  const primaryCategory = appCategories.find(
    (category) => category.classification === Classification.Primary,
  )

  const gradient = `radial-gradient(70% 100% at 0% 0%, ${hexToRgba(primaryColor ?? '#FFFFFF', 0.35)} 0%, ${hexToRgba(primaryColor ?? '#FFFFFF', 0)} 80%),
              radial-gradient(70% 100% at 100% 0%, ${hexToRgba(secondaryColor ?? '#FFFFFF', 0.35)} 0%, ${hexToRgba(secondaryColor ?? '#FFFFFF', 0)} 80%),
              radial-gradient(100% 100% at 50% 0%, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) 80%, rgba(0, 0, 0, 0) 100%),
              #000`

  return (
    <Hero gradient={gradient} className="pt-14">
      {({ animations }) => (
        <>
          <Container height="fill" className="relative z-10 flex h-full w-full items-end pb-24">
            <Motion>
              <m.div style={animations} className="h-fit">
                {children}
              </m.div>
            </Motion>
          </Container>

          {primaryCategory && (
            <Link
              href={`/best/${primaryCategory.category.slug}`}
              className="absolute right-1/2 top-28 z-10 translate-x-1/2 sm:right-16 sm:translate-x-0"
            >
              <Award
                category={primaryCategory.category.name}
                recommendation={primaryCategory.recommendation as Recommendation}
                overlay={true}
                nested={true}
              />
            </Link>
          )}

          <VideoControls
            playing={playing}
            setPlaying={setPlaying}
            muted={muted}
            setMuted={setMuted}
            setModal={setModal}
            customClick={() =>
              scrollToSection({ id: 'video-player', offset: 100, animate: true, hideHash: true })
            }
          />
        </>
      )}
    </Hero>
  )
}
